// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Divider, Form, message, Modal, Popconfirm, Spin } from "antd";
import { EditOutlined, LoadingOutlined, SaveOutlined } from "@ant-design/icons";
import {
  BroadcastListInfoResponseDTO,
  BroadcastListSub,
  BroadcastListWriteRequestDTO
} from "../../../../api/backend-openapi";
import { formItemBlockLayout } from "../../../../utils/Constant";
import { navigate } from "@reach/router";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  StopOutlined
} from "@ant-design/icons/lib";
import DateFormat from "../../../../utils/DateFormat";
import {
  requestDeleteBroadcastList,
  requestGetBroadcastList,
  requestUpdateBroadcastList
} from "../../../../state/broadcastLists/BroadcastListsEffects";
import BroadcastListSettingsFormSection from "./BroadcastListSettingsFormSection";
import { displayError } from "../../../../utils/displayError";
import { BroadcastListEditFormProps } from "../../../types/BroadcastListTypes";
import { showConfirm } from "../../../components/ConfirmModal";
import i18n from "i18next";
import { ValidatorResult } from "../../../types/Types";
import { validate } from "../../../../validator/BroadcastListValidator";
const { confirm } = Modal;

const formatEmail = (subItem: BroadcastListSub): string => {
  return subItem.email ? subItem.email : subItem.sub.toString();
};

const BroadcastListForm: React.FunctionComponent<BroadcastListEditFormProps> = (
  props: BroadcastListEditFormProps
) => {
  const { broadcastListId, editMode, setEditMode, canDelete, canEdit } = props;

  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [
    broadcastListDetails,
    setBroadcastListDetails
  ] = useState<BroadcastListInfoResponseDTO | null>(null);
  const [fields, setFields] = useState<any[]>([]);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<JSX.Element | undefined>(
    undefined
  );

  const resetFormFields = (data: {
    id: string;
    name: string;
    branch?: string;
    emails: string[];
    modificationDate?: Date;
    creationDate?: Date;
  }) => {
    setFields([
      {
        name: ["id"],
        value: data.id
      },
      {
        name: ["name"],
        value: data.name
      },
      {
        name: ["branch"],
        value: data.branch
      },
      {
        name: ["emails"],
        value: data.emails
      },
      {
        name: ["creationDate"],
        value: data.creationDate
          ? DateFormat.standardDate(data.creationDate)
          : "00/00/00"
      },
      {
        name: ["modificationDate"],
        value: data.modificationDate
          ? DateFormat.standardDate(data.modificationDate)
          : "00/00/00"
      }
    ]);
  };

  useEffect(() => {
    if (broadcastListId) {
      requestGetBroadcastList(broadcastListId);
    }
  }, [broadcastListId]);

  useEffect(() => {
    return requestGetBroadcastList.done.watch(({ result }) => {
      if (result.ok) {
        if (result.data) {
          setBroadcastListDetails(result.data);
          const emailsFormattedToSelect = result.data.subs.map(formatEmail);
          resetFormFields({ ...result.data, emails: emailsFormattedToSelect });
        } else {
          displayError(result.errorMessage || "Error!", false);
        }
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    return requestDeleteBroadcastList.done.watch(({ result }) => {
      if (result.ok) {
        message.success(t("broadcast_list.global_buttons.delete.success"));
        navigate("/broadcastLists");
      } else {
        displayError("broadcast_list.global_buttons.delete.error");
      }
    });
  });

  useEffect(() => {
    return requestUpdateBroadcastList.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        setEditMode(false);
        setBroadcastListDetails(result.data);
        const emailsFormattedToSelect = result.data.subs.map(formatEmail);
        resetFormFields({ ...result.data, emails: emailsFormattedToSelect });
        message.success(t("edit_broadcast_list.success"));
      } else {
        if (
          result.errorCode &&
          ["other_branch", "unknown_email"].includes(result.errorCode)
        ) {
          const emails = result.errorMessage?.split(",");
          setEmailError(
            <p className="text-red mb-0">
              {t(`broadcast_list.emails.errors.${result.errorCode}`)}
              <ul>
                {emails?.map(email => (
                  <li key={email}>{email}</li>
                ))}
              </ul>
            </p>
          );
        }
        displayError(
          t("broadcast_list.emails.errors.generic") || "Error!",
          false
        );
      }
    });
  });

  const spinIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  const confirmationPopup = async () => {
    return new Promise<boolean>(resolve => {
      confirm({
        title: t("edit_broadcast_list.modal.title"),
        content: t("edit_broadcast_list.modal.description"),
        okText: t("edit_broadcast_list.modal.yes"),
        cancelText: t("edit_broadcast_list.modal.no"),
        centered: true,
        onOk() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        }
      });
    });
  };

  const handleSubmit = (values: any) => {
    const validationResult: ValidatorResult = validate(values.emails);
    if (!validationResult.validate) {
      displayError(validationResult.message);
    } else {
      confirmationPopup().then((confirmed: boolean) => {
        if (confirmed && broadcastListDetails) {
          const broadcastListToUpdate: BroadcastListWriteRequestDTO = {
            name: values.name,
            branch: values.branch,
            emails: values.emails
          };
          requestUpdateBroadcastList({
            id: broadcastListDetails.id,
            dto: broadcastListToUpdate
          });
          setButtonLoading(true);
          setEmailError(undefined);
        }
      });
    }
  };

  const handleSave = () => {
    form
      .validateFields()
      .then(values => {
        handleSubmit(values);
      })
      .catch(() => {
        displayError("global.on_finish_failed");
      });
  };

  const onFinishFailed = ({ errorFields }: any) => {
    displayError("global.on_finish_failed");
    form.scrollToField(errorFields[0].name);
  };

  return (
    <Form
      form={form}
      {...formItemBlockLayout}
      onFinish={handleSave}
      onFinishFailed={onFinishFailed}
      fields={fields}
    >
      <>
        {buttonLoading ? (
          <div style={{ textAlign: "center" }}>
            <Spin indicator={spinIcon} />
          </div>
        ) : broadcastListDetails ? (
          <>
            <div className="form-first-item-sticky-top-container">
              <div className="form-first-item-sticky-top-content">
                <Divider orientation="left">
                  <h3 className="text-secondary mb-0">
                    {t("broadcast_list.actions")}
                  </h3>
                </Divider>
                <div className="d-flex align-items-center justify-content-between flex-wrap px-3">
                  <div className="my-3">
                    <div className="d-flex align-items-center justify-content-center flex-wrap">
                      {!editMode && (
                        <Button
                          htmlType="reset"
                          className="m-2"
                          onClick={() => navigate("/broadcastLists")}
                        >
                          <ArrowLeftOutlined />{" "}
                          {t("edit_broadcast_list.return")}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div>
                    {editMode ? (
                      <>
                        <div className="text-center m-2">
                          <Popconfirm
                            title={t("global.cancel_confirmation")}
                            okText={t("global.yes")}
                            cancelText={t("global.no")}
                            onConfirm={() => {
                              setEditMode(false);
                              form.resetFields();
                              const emailsFormattedToSelect = broadcastListDetails.subs.map(
                                formatEmail
                              );
                              resetFormFields({
                                ...broadcastListDetails,
                                emails: emailsFormattedToSelect
                              });
                            }}
                            placement="top"
                            icon={<QuestionCircleOutlined />}
                            className="m-2"
                          >
                            <Button htmlType="reset">
                              <StopOutlined /> {t("edit_broadcast_list.cancel")}
                            </Button>
                          </Popconfirm>
                          <Button
                            className="btn-primary m-2"
                            onClick={handleSave}
                            loading={buttonLoading}
                          >
                            <SaveOutlined /> {t("edit_broadcast_list.save")}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="d-flex align-items-center justify-content-center flex-wrap">
                          {canEdit && (
                            <Button
                              htmlType="reset"
                              className="btn-primary m-2"
                              onClick={() => setEditMode(true)}
                            >
                              <EditOutlined /> {t("edit_broadcast_list.button")}
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              className="btn-danger m-2"
                              onClick={() =>
                                showConfirm(
                                  i18n.t("broadcast_list.confirmation.delete", {
                                    broadcastList: broadcastListDetails.name
                                  }),
                                  () => {
                                    requestDeleteBroadcastList(
                                      broadcastListDetails
                                    );
                                  }
                                )
                              }
                            >
                              <DeleteOutlined />{" "}
                              {t("edit_broadcast_list.delete")}
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <Divider orientation="left">
              <h3 className="text-secondary mb-0">
                {t("broadcast_list.settings")}
              </h3>
            </Divider>
            <BroadcastListSettingsFormSection
              broadcastList={broadcastListDetails}
              editMode={editMode}
              showRequired={editMode}
              form={form}
              emailError={emailError}
            />
          </>
        ) : (
          <div>{t("broadcast_list.not_found")}</div>
        )}
      </>
    </Form>
  );
};

export default BroadcastListForm;
